import BaseService           from '@/core/services/BaseService';
import TransportDowntime     from '@/domains/TransportDowntime/TransportDowntime';
import TransportDowntimesDAO from '@/domains/TransportDowntime/TransportDowntimesDAO';
import ContextMenuItem       from '@/lib/components/VContextMenu/ContextMenuItem';
import TransportOrdersService from '@/domains/TransportOrders/TransportOrdersService';
import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import Core                  from '@/core/Core';

class TransportDowntimesService extends BaseService {

  static get dao() {
    return TransportDowntimesDAO;
  }

  /** Загружает список простоев по ЗТК */
  static async getList(
    filters?: Parameters<typeof TransportDowntimesDAO.getList>[0]
  ): Promise<TransportDowntime[]> {
    const response = await this.dao.getList(filters);
    return this.core.utils.hydrateArray(response.data, TransportDowntime.fromServer);
  }

  /**
   * Список простоев для реестра через DataGridLoader.
   * Лоадер зовёт метод без контекста — поэтому без this.
   * Пагинацию и сортировку бэк пока не принимает: отрезаем их и отдаём всё
   */
  static async getListPaginated(
    params?: Parameters<typeof TransportDowntimesDAO.getList>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    },
  ): Promise<{
    data: TransportDowntime[];
    pagination: RpcDataPagination | null;
  }> {
    const { pagination, sort, ...filters } = params ?? {};

    const response = await TransportDowntimesDAO.getList(filters);

    return {
      data: Core.utils.hydrateArray(response.data, TransportDowntime.fromServer),
      pagination: null,
    };
  }

  /** Загружает один простой по ID */
  static async get(id: number): Promise<TransportDowntime | null> {
    const response = await this.dao.getList({ id });
    const list = this.core.utils.hydrateArray(response.data, TransportDowntime.fromServer);
    return list[0] ?? null;
  }

  /** Создаёт или обновляет простой */
  static async upsert(transportDowntime: TransportDowntime): Promise<number> {
    const response = await this.dao.upsert(transportDowntime);

    if (response.ok) {
      this.core.showNotification.success('Простой успешно сохранён');
    }

    return response.data;
  }

  /** Удаляет простой по ID */
  static async delete(id: number): Promise<boolean> {
    const response = await this.dao.delete(id);

    if (response.ok) {
      this.core.showNotification.success('Простой успешно удалён');
    }

    return response.ok;
  }

  /** Удаляет простой с подтверждением. Возвращает true, если удаление произошло. */
  static async deleteWithConfirm(downtime: TransportDowntime): Promise<boolean> {
    const confirmed = await this.core.confirm('Удалить простой?');

    if (confirmed === true) {
      const isDeleted = await this.delete(downtime.id);

      if (isDeleted === true) {
        downtime.isDeleted = true;
      }

      return isDeleted;
    }
    else {
      return false;
    }
  }

  /**
   * Открыть карточку простоя.
   * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
   */
  static showTransportDowntimeCard(downtime: TransportDowntime): Promise<boolean> {
    return this.core.dialogs.TransportDowntimeCard.showAsPromise({ downtime });
  }

  /**
   * Пункты контекстного меню простоя. Callback возвращает true, только если
   * что-то изменилось — грид на :data узнаёт об этом по contextMenuItem:done.
   */
  static get contextMenu(): ContextMenuItem<TransportDowntime>[] {
    return [
      new ContextMenuItem<TransportDowntime>(
        'Открыть',
        'open_in_new',
        (downtime: TransportDowntime) => { return TransportDowntimesService.showTransportDowntimeCard(downtime); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),

      new ContextMenuItem<TransportDowntime>(
        'Открыть ЗТК',
        'open_in_new',
        (downtime: TransportDowntime) => { return TransportOrdersService.showTransportOrderDialog(downtime.transportOrder.id); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),

      new ContextMenuItem<TransportDowntime>(
        'Удалить',
        'delete',
        (downtime: TransportDowntime) => { return TransportDowntimesService.deleteWithConfirm(downtime); },
      ),
    ];
  }
}

export default TransportDowntimesService;